import { useCallback, useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * Catalog pagination state lives in the URL (`?page=N`), not in component state.
 *
 * Keeping it in `useState` meant page 2 and beyond had no address at all: crawlers
 * could never reach them, and a reload or shared link always dropped back to page 1.
 *
 * `resetKey` is the search term — when it changes the catalog restarts at page 1.
 */
export function useCatalogPage(resetKey?: string) {
    const { pathname } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const raw = searchParams.get("page");
    const parsed = raw ? parseInt(raw, 10) : 1;
    const page = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;

    // Skip the first run so an inbound /valorant-pro-crosshairs?page=3 is not stripped on mount.
    const mounted = useRef(false);
    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("page");
                return next;
            },
            { replace: true }
        );
        // resetKey is the only real trigger; setSearchParams identity is not stable.
    }, [resetKey]);

    const buildHref = useCallback(
        (p: number) => (p <= 1 ? pathname : `${pathname}?page=${p}`),
        [pathname]
    );

    return { page, buildHref };
}
